"use client";

import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function TeamAvatars({ teamMembers, isDarkBackground = false, maxVisible = 4, size = "sm" }) {
  if (!teamMembers || teamMembers.length === 0) {
    return (
      <div className={`text-xs ${
        isDarkBackground ? 'text-gray-400' : 'text-muted-foreground'
      }`}>
        No members yet
      </div>
    );
  }

  const avatarSize = size === "sm" ? "h-6 w-6" : "h-10 w-10";
  const textSize = size === "sm" ? "text-xs" : "text-sm";

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex items-center -space-x-2">
        {teamMembers.slice(0, maxVisible).map((member, index) => {
          const initials = member.initials ||
            `${(member.name?.split(' ')[0]?.[0] || member.email?.[0] || 'U')}${(member.name?.split(' ')[1]?.[0] || '')}`.toUpperCase();

          return (
            <Tooltip key={member.id || index}>
              <TooltipTrigger asChild>
                <div
                  className="relative transition-transform hover:-translate-y-1 cursor-pointer"
                  style={{ zIndex: index }}
                >
                  <Avatar className={`${avatarSize} border-2 ${
                    isDarkBackground ? 'border-gray-700' : 'border-white'
                  }`}>
                    <AvatarImage
                      src={member.avatar}
                      alt={member.name}
                      className="object-cover"
                    />
                    <AvatarFallback className={textSize}>
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={10}>
                <div className="space-y-1">
                  <p className="font-semibold">{member.name}</p>
                  <p className="text-xs text-muted-foreground">{member.email}</p>
                  {member.role === 'Owner' && (
                    <Badge className="text-xs" variant="secondary">Owner</Badge>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
        {teamMembers.length > maxVisible && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="relative transition-transform hover:-translate-y-1 cursor-pointer"
                style={{ zIndex: maxVisible }}
              >
                <Avatar className={`${avatarSize} border-2 ${
                  isDarkBackground ? 'border-gray-700 bg-gray-700' : 'border-white bg-gray-200'
                }`}>
                  <AvatarFallback className={`${textSize} ${
                    isDarkBackground ? 'text-gray-200' : 'text-gray-700'
                  } font-semibold`}>
                    +{teamMembers.length - maxVisible}
                  </AvatarFallback>
                </Avatar>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={10}>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <p className="text-xs text-muted-foreground font-semibold mb-2">
                  {teamMembers.length - maxVisible} more member{teamMembers.length - maxVisible !== 1 ? 's' : ''}:
                </p>
                {teamMembers.slice(maxVisible).map((member) => {
                  const initials = member.initials ||
                    `${(member.name?.split(' ')[0]?.[0] || member.email?.[0] || 'U')}${(member.name?.split(' ')[1]?.[0] || '')}`.toUpperCase();

                  return (
                    <div key={member.id} className="space-y-1 pb-2">
                      <p className="font-semibold">{member.name}</p>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                      {member.role === 'owner' && (
                        <Badge className="text-xs" variant="secondary">Owner</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
